/* ===== SUPABASE_AUTH_UPDATE =====
 * Updated: Authentication now uses Supabase instead of JWT
 * Subscription checks are temporarily disabled
 * ===== */

import { supabase } from '@/config/supabase'
import posthog from 'posthog-js'
import axios from 'axios'

export default {
  namespaced: true,
  state: {
    session: null,
    user: null,
    loading: false
  },
  getters: {
    isAuthenticated: (state) => !!state.session,
    jwt: (state) => state.session ? state.session.access_token : null, // For backward compatibility
    hasActiveSubscription: (state) => {
      /* ===== SUBSCRIPTION_FEATURE_START ===== */
      // return state.user && state.user.has_active_subscription;
      /* ===== SUBSCRIPTION_FEATURE_END ===== */
      // Free access for all users
      return true;
    },
    subscriptionStatus: (state) => {
      /* ===== SUBSCRIPTION_FEATURE_START ===== */
      // return state.user ? state.user.subscription_status : null;
      /* ===== SUBSCRIPTION_FEATURE_END ===== */
      return 'free'; // All users have free access
    },
    currentUser: (state) => state.user,
    accessToken: (state) => state.session ? state.session.access_token : null
  },
  mutations: {
    setSession(state, session) {
      state.session = session
      if (session) {
        // Store access_token for backward compatibility
        if (session.access_token) {
          localStorage.setItem('auth_token', session.access_token)
        }
        // Store full session (including refresh_token) for token refresh
        localStorage.setItem('supabase_session', JSON.stringify(session))
      }
    },
    setSessionWithStorage(state, session) {
      // Same as setSession - alias for compatibility
      state.session = session
      if (session) {
        // Store access_token for backward compatibility
        if (session.access_token) {
          localStorage.setItem('auth_token', session.access_token)
        }
        // Store full session (including refresh_token) for token refresh
        localStorage.setItem('supabase_session', JSON.stringify(session))
      }
    },
    setUser(state, user) {
      state.user = user
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    logout(state) {
      state.session = null
      state.user = null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('supabase_session')
    }
  },
  actions: {
    async login({ commit, dispatch }, { email, password }) {
      commit('setLoading', true)
      try {
        // Clear any existing API keys before login
        dispatch('apiKeys/clearApiKeys', null, { root: true })

        // Login via backend which uses Supabase
        const response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/api/users/login`, {
          email,
          password
        })

        if (response.data.success) {
          const { user, session, token } = response.data

          const sessionToStore = session || { access_token: token }
          commit('setSession', sessionToStore)
          commit('setUser', user)

          // Tell Supabase about this session to prevent auto sign-out
          if (session && session.access_token && session.refresh_token) {
            // console.log('📡 Setting session in Supabase...');
            const { data, error } = await supabase.auth.setSession(session)
            if (error) {
              console.error('❌ Failed to set session in Supabase:', error);
            } else {
              // console.log('✅ Session set in Supabase successfully');
            }
          } else {
            // console.warn('⚠️ Session missing refresh_token, not setting in Supabase');
          }

          // Identify user in PostHog
          if (user && posthog) {
            posthog.identify(user.id, {
              email: user.email,
              first_name: user.first_name,
              last_name: user.last_name,
              created_at: new Date().toISOString()
            });

            // Track login event
            posthog.capture('user_logged_in', {
              method: 'email',
              user_id: user.id,
              email: user.email
            });
          }

          // Force refresh API keys for the new user
          dispatch('apiKeys/fetchGoogleMapsKey', null, { root: true })

          return { success: true, user }
        } else {
          throw new Error(response.data.message || 'Login failed')
        }
      } catch (error) {
        console.error('Login error:', error)
        const message = (error.response && error.response.data && error.response.data.message) || error.message || 'Login failed'
        return { success: false, error: message }
      } finally {
        commit('setLoading', false)
      }
    },

    async signup({ commit }, payload) {
      commit('setLoading', true)
      try {
        // Register via backend which uses Supabase
        const response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/api/users/register`, {
          email: payload.email,
          password: payload.password,
          first_name: payload.first_name,
          last_name: payload.last_name,
          phone: payload.phone
          // Note: plan_type removed - no subscription required
        })

        if (response.data.success) {
          const { user, session, access_token } = response.data

          // Auto-login after registration
          if (session || access_token) {
            commit('setSession', session || { access_token })
            commit('setUser', user)
          }

          // Identify new user in PostHog
          if (user && posthog) {
            posthog.identify(user.id, {
              email: user.email,
              first_name: user.first_name,
              last_name: user.last_name,
              created_at: new Date().toISOString()
            });

            // Track signup event
            posthog.capture('user_signed_up', {
              method: 'email',
              user_id: user.id,
              email: user.email
            });
          }

          /* ===== SUBSCRIPTION_FEATURE_START ===== */
          // Previous code redirected to payment:
          // if (response.data.requiresPayment) {
          //   return { success: true, requiresPayment: true, checkoutUrl: response.data.checkoutUrl }
          // }
          /* ===== SUBSCRIPTION_FEATURE_END ===== */

          return { success: true, user }
        } else {
          throw new Error(response.data.message || 'Registration failed')
        }
      } catch (error) {
        console.error('Signup error:', error)
        const message = (error.response && error.response.data && error.response.data.message) || error.message || 'Registration failed'
        return { success: false, error: message }
      } finally {
        commit('setLoading', false)
      }
    },

    async logout({ commit, dispatch }) {
      try {
        // Sign out from Supabase
        await supabase.auth.signOut()

        // Reset PostHog
        if (posthog) {
          posthog.reset()
        }

        // Clear API keys from store
        dispatch('apiKeys/clearApiKeys', null, { root: true })

        commit('logout')
        return { success: true }
      } catch (error) {
        console.error('Logout error:', error)
        // Even if there's an error, clear local state
        dispatch('apiKeys/clearAllKeys', null, { root: true })
        commit('logout')
        return { success: true }
      }
    },

    async refreshToken({ commit, state }) {
      try {
        // console.log('🔄 Attempting to refresh token...');

        // Get current session from state
        const currentSession = state.session;

        console.log('📦 Current session:', {
          hasSession: !!currentSession,
          hasAccessToken: !!(currentSession && currentSession.access_token),
          hasRefreshToken: !!(currentSession && currentSession.refresh_token),
          accessTokenLength: currentSession && currentSession.access_token ? currentSession.access_token.length : 0,
          refreshTokenLength: currentSession && currentSession.refresh_token ? currentSession.refresh_token.length : 0
        });

        if (!currentSession || !currentSession.refresh_token) {
          console.error('❌ No refresh token available');
          console.error('Session state:', currentSession);
          return { success: false, error: 'No refresh token' };
        }

        // Use Supabase's refresh with the current session
        // console.log('📡 Calling Supabase refreshSession...');
        const { data: { session }, error } = await supabase.auth.refreshSession(currentSession);

        if (error) {
          console.error('❌ Token refresh failed:', error);
          console.error('Error details:', {
            message: error.message,
            status: error.status,
            code: error.code
          });
          return { success: false, error };
        }

        if (session) {
          // console.log('✅ Token refreshed successfully!');
          // console.log('New token length:', session.access_token ? session.access_token.length : 0);

          // Update stored session and token
          commit('setSessionWithStorage', session);

          // Get updated user data
          const { data: { user } } = await supabase.auth.getUser(session.access_token);
          if (user) {
            const userData = {
              id: user.id,
              email: user.email,
              first_name: (user.user_metadata && user.user_metadata.first_name) || '',
              last_name: (user.user_metadata && user.user_metadata.last_name) || '',
              phone: (user.user_metadata && user.user_metadata.phone) || '',
              has_active_subscription: true, // Free access for all
              user_metadata: user.user_metadata || {}, // Include full metadata for DataForSEO
              raw_user_meta_data: user.raw_user_meta_data || user.user_metadata || {} // Include raw metadata too
            };
            commit('setUser', userData);
          }

          return { success: true, session };
        }

        console.error('❌ No session returned from refresh');
        return { success: false, error: 'No session returned' };
      } catch (error) {
        console.error('❌ Token refresh exception:', error);
        return { success: false, error };
      }
    },

    async checkAuth({ commit, state, dispatch }) {
      try {
        // First check if we have a full session in localStorage
        const storedSessionStr = localStorage.getItem('supabase_session')
        const storedToken = localStorage.getItem('auth_token')

        if (storedSessionStr && !state.session) {
          // We have a full session stored, restore it (includes refresh_token)
          try {
            const storedSession = JSON.parse(storedSessionStr)
            commit('setSession', storedSession)

            // IMPORTANT: Tell Supabase about this session so it doesn't fire SIGNED_OUT
            await supabase.auth.setSession(storedSession)
          } catch (parseError) {
            console.error('Failed to parse stored session:', parseError)
            // Fallback to just access_token
            if (storedToken) {
              commit('setSession', { access_token: storedToken })
            }
          }
        } else if (storedToken && !state.session) {
          // Fallback: We have a token but no full session, restore what we can
          commit('setSession', { access_token: storedToken })
        }

        // Check if we have a valid Supabase session
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error) {
          // console.log('Session check error:', error)
          // Don't throw, just log
        }

        if (session) {
          // Check if token is about to expire (within 10 minutes)
          const expiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
          const now = new Date();
          const tenMinutesFromNow = new Date(now.getTime() + 10 * 60000);

          if (expiresAt && expiresAt < tenMinutesFromNow) {
            // Token is about to expire, refresh it
            // console.log('Token expiring soon, proactively refreshing...');
            const refreshResult = await dispatch('refreshToken');
            if (refreshResult.success && refreshResult.session) {
              // Use the new session from refresh
              commit('setSessionWithStorage', refreshResult.session);
              return true;
            }
          }

          commit('setSession', session)

          // Get user data from backend or Supabase
          const { data: { user } } = await supabase.auth.getUser()
          if (user) {
            // Enhance with profile data if needed
            const userData = {
              id: user.id,
              email: user.email,
              first_name: (user.user_metadata && user.user_metadata.first_name) || '',
              last_name: (user.user_metadata && user.user_metadata.last_name) || '',
              phone: (user.user_metadata && user.user_metadata.phone) || '',
              has_active_subscription: true, // Free access for all
              user_metadata: user.user_metadata || {}, // Include full metadata for DataForSEO
              raw_user_meta_data: user.raw_user_meta_data || user.user_metadata || {} // Include raw metadata too
            }
            commit('setUser', userData)
          }

          return true
        } else if (storedToken) {
          // We have a token but no Supabase session
          // This might happen right after signup
          // Try to get user data using the token
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/api/users/profile`, {
              headers: {
                'Authorization': `Bearer ${storedToken}`
              }
            })

            if (response.data) {
              commit('setUser', response.data)
              return true
            }
          } catch (profileError) {
            // console.log('Profile fetch error:', profileError)
          }
        }

        return false
      } catch (error) {
        console.error('Auth check error:', error)
        // Don't automatically logout, just return false
        return false
      }
    },

    async updateProfile({ commit, state }, profileData) {
      try {
        const response = await axios.put(`${process.env.VUE_APP_API_ENDPOINT}/api/users/profile`, profileData)

        if (response.data.success) {
          const updatedUser = { ...state.user, ...response.data.user }
          commit('setUser', updatedUser)
          return { success: true }
        }

        throw new Error(response.data.message || 'Failed to update profile')
      } catch (error) {
        console.error('Profile update error:', error)
        return {
          success: false,
          error: (error.response && error.response.data && error.response.data.message) || error.message
        }
      }
    }
  }
}

/* ===== OLD_JWT_CODE_REFERENCE =====
 * The original JWT-based authentication has been replaced with Supabase
 * Original code included:
 * - JWT token management
 * - Direct axios calls without Supabase
 * - Subscription status checks
 * ===== */